import { Button, TooltipTrigger } from '@toggle/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import {
  ContinueContextButtonKeys,
  useRemoteStorage,
} from '~/hooks/use-remote-storage';
import { DocumentResponse } from '~/stores/use-chat/assistant-response.type';
import { useChat } from '~/stores/use-chat/useChat';
import { CITATION_GROUP_ID } from '~/views/intel/document-sources-vew/DocumentSourcesView';

import * as S from './ContinueContextButton.styles';
import { FirstViewTooltip } from './FirstViewTooltip';

export interface ContinueContextButtonProps {
  flag: ContinueContextButtonKeys;
  messageIndex: number;
}

export const ContinueContextButton = ({
  flag,
  messageIndex,
}: ContinueContextButtonProps) => {
  const { t } = useTranslation('intel');
  const [params, setParams] = useSearchParams();
  const {
    sendMessage,
    clearDocumentContextMessages,
    documentContextMessages,
    resetSession,
    messages,
  } = useChat(state => ({
    resetSession: state.resetSession,
    sendMessage: state.sendMessage,
    clearDocumentContextMessages: state.clearDocumentContextMessages,
    documentContextMessages: state.documentContextMessages,
    messages: state.messages,
  }));
  const items = useRemoteStorage(state => state.items);
  const firstView = !items?.[flag];
  const [open, setOpen] = useState(false);

  const handleClearContext = () => {
    resetSession(true);
    clearDocumentContextMessages();
    const newParams = new URLSearchParams(params);
    newParams.delete(CITATION_GROUP_ID);
    setParams(newParams);

    const currentMessage =
      documentContextMessages[documentContextMessages.length - 1];

    if (currentMessage) {
      sendMessage({
        question: currentMessage.question,
        target: 'document-assistant',
      });
    }
  };

  const isInDocsView = flag === 'CHAT_DOCUMENT_SEARCH_CONTEXT_SEEN_DOC_VIEW';

  const children = (
    <S.StyledBadge
      $firstView={firstView}
      $isInDocsView={isInDocsView}
      label={
        isInDocsView
          ? t('intel:context')
          : t('intel:context', { context: 'continue' })
      }
      size="medium"
      leftIcon="BookIdea"
      badgeStyle="outline"
    />
  );

  if (firstView) {
    return <FirstViewTooltip flag={flag}>{children}</FirstViewTooltip>;
  }

  const currentMessage = messages[messageIndex];
  const contextMessageIndex = documentContextMessages.findIndex(
    m => m.id === currentMessage.id
  );
  const contextMessage = documentContextMessages[contextMessageIndex];

  const contextList = documentContextMessages
    .slice(0, contextMessageIndex + 1)
    .map(m => m.question);
  const docsOverview = (contextMessage?.reply as DocumentResponse[])?.[0]?.data
    .overview;

  const documentCount = documentContextMessages[
    documentContextMessages.length - 1
  ]?.reply?.length
    ? docsOverview?.documents ?? 0
    : 0;
  const companyCount = docsOverview?.companies ?? 0;

  const resolvedDocumentCount = t('documentCount', {
    documentCount: documentCount,
    count: documentCount,
  });
  const resolvedCompanyCount = t('companyCount', {
    companyCount: companyCount,
    count: companyCount,
  });

  return (
    <TooltipContainer
      label={
        <div>
          <S.TooltipTitle>
            {t('intel:document-message.baseContext')}
          </S.TooltipTitle>
          <S.TooltipTextSoft>
            {t('intel:contextCount', {
              documentCount: resolvedDocumentCount,
              companyCount: resolvedCompanyCount,
            })}
          </S.TooltipTextSoft>
          <S.TooltipTextList>
            {contextList.map(question => (
              <S.TooltipTextListItem key={question}>
                {question}
              </S.TooltipTextListItem>
            ))}
          </S.TooltipTextList>
          <Button
            data-testid="clear-context-button"
            label={t('intel:context', { context: 'clear' })}
            size="small"
            onClick={handleClearContext}
          />
        </div>
      }
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      trigger={[TooltipTrigger.Click]}
      isOpen={open}
      offsetY={4}
    >
      {children}
    </TooltipContainer>
  );
};
